import { ReactElement } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { FC90K_CONVERSION_FACTOR } from '@domainConstants'
import { Edit, Link } from '@mui/icons-material'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { UnitConvertedField } from '@ui/forms'
import { growFieldInnerSx, growFieldSx } from '../constants'

/** Seed for overriding: the documented standard value (C24 – 2.5 N/mm²). */
const FC90K_OVERRIDE_SEED = 2.5 * FC90K_CONVERSION_FACTOR

/**
 * fc,90,k staged for every support. null = the value is derived per support
 * from its cross section/assembly — those derived values differ per member, so
 * unlike the single-element pane no derived number is previewed here.
 */
const SharedSupportFC90KField = (): ReactElement => {
  const { setValue } = useFormContext()
  const fc90k: number | null | undefined = useWatch({ name: 'sharedSettings.f_c_90_k' })

  if (fc90k == null) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Wert wird je Auflager aus Bauteil/Aufbau bezogen
        </Typography>
        <Tooltip title="Wert überschreiben (Vorbelegung: Standard C24 – 2.5 N/mm²)">
          <IconButton
            size="small"
            data-cy="support-compression-fc90k-override"
            onClick={() =>
              setValue('sharedSettings.f_c_90_k', FC90K_OVERRIDE_SEED, {
                shouldDirty: true,
                shouldTouch: true,
              })
            }
          >
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    )
  }

  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
      <Box sx={growFieldSx(90)}>
        <UnitConvertedField
          name="sharedSettings.f_c_90_k"
          type="number"
          unitLabel="N/mm²"
          conversionFactor={FC90K_CONVERSION_FACTOR}
          sx={growFieldInnerSx}
          hideFormControl
          data-cy="support-compression-fc90k"
        />
      </Box>
      <Tooltip title="Wert je Auflager wieder aus Bauteil/Aufbau beziehen">
        <IconButton
          size="small"
          data-cy="support-compression-fc90k-reset"
          onClick={() =>
            setValue('sharedSettings.f_c_90_k', null, { shouldDirty: true, shouldTouch: true })
          }
        >
          <Link fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

export default SharedSupportFC90KField
